import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Palette, Layers, Split, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { AdvancedColorPicker } from './AdvancedColorPicker';
import { GradientEditor } from './GradientEditor';

interface PageBackground {
  type: 'solid' | 'gradient' | 'split';
  color?: string;
  gradient?: {
    type: 'linear' | 'radial' | 'conic';
    angle?: number;
    stops: Array<{
      color: string;
      position: number;
    }>;
    centerX?: number;
    centerY?: number;
    shape?: 'circle' | 'ellipse';
  };
  split?: {
    direction: 'horizontal' | 'vertical';
    position: number;
    first: {
      type: 'solid' | 'gradient';
      color?: string;
      gradient?: PageBackground['gradient'];
    };
    second: {
      type: 'solid' | 'gradient';
      color?: string;
      gradient?: PageBackground['gradient'];
    };
  };
}

interface PageBackgroundSidebarProps {
  background: PageBackground;
  onChange: (background: PageBackground) => void;
  onClose: () => void;
  isOpen: boolean;
}

// Quick color presets for easy access
const QUICK_COLORS = [
  '#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6', '#ced4da', '#adb5bd',
  '#6c757d', '#495057', '#343a40', '#212529', '#000000',
  '#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6', '#42a5f5', '#2196f3',
  '#1e88e5', '#1976d2', '#1565c0', '#0d47a1',
  '#e8f5e8', '#c8e6c9', '#a5d6a7', '#81c784', '#66bb6a', '#4caf50',
  '#43a047', '#388e3c', '#2e7d32', '#1b5e20',
  '#fff3e0', '#ffe0b2', '#ffcc80', '#ffb74d', '#ffa726', '#ff9800',
  '#fb8c00', '#f57c00', '#ef6c00', '#e65100',
  '#fce4ec', '#f8bbd9', '#f48fb1', '#f06292', '#ec407a', '#e91e63',
  '#d81b60', '#c2185b', '#ad1457', '#880e4f'
];

// Generate CSS from background data
const generateBackgroundCSS = (background: PageBackground): string => {
  switch (background.type) {
    case 'solid':
      return background.color || '#ffffff';
    
    case 'gradient':
      if (!background.gradient) return '#ffffff';
      const { type, angle = 0, stops, centerX = 50, centerY = 50, shape = 'circle' } = background.gradient;
      const stopList = stops.map(stop => `${stop.color} ${stop.position}%`).join(', ');
      
      switch (type) {
        case 'linear':
          return `linear-gradient(${angle}deg, ${stopList})`;
        case 'radial':
          return `radial-gradient(${shape} at ${centerX}% ${centerY}%, ${stopList})`;
        case 'conic':
          return `conic-gradient(from ${angle}deg at ${centerX}% ${centerY}%, ${stopList})`;
        default:
          return '#ffffff';
      }
    
    case 'split':
      if (!background.split) return '#ffffff';
      const { direction, position, first, second } = background.split;
      const firstBg = first.type === 'solid' ? first.color : generateBackgroundCSS({ type: 'gradient', gradient: first.gradient });
      const secondBg = second.type === 'solid' ? second.color : generateBackgroundCSS({ type: 'gradient', gradient: second.gradient });
      
      if (direction === 'horizontal') {
        return `linear-gradient(180deg, ${firstBg} ${position}%, ${secondBg} ${position}%)`;
      } else {
        return `linear-gradient(90deg, ${firstBg} ${position}%, ${secondBg} ${position}%)`;
      }
    
    default:
      return '#ffffff';
  }
};

export const PageBackgroundSidebar: React.FC<PageBackgroundSidebarProps> = ({
  background,
  onChange,
  onClose,
  isOpen
}) => {
  const [activeSection, setActiveSection] = useState<'colors' | 'gradients' | 'split'>('colors');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showGradientEditor, setShowGradientEditor] = useState(false);

  const handleQuickColorSelect = useCallback((color: string) => {
    onChange({
      type: 'solid',
      color
    });
  }, [onChange]);

  const handleGradientChange = useCallback((gradient: PageBackground['gradient']) => {
    onChange({
      type: 'gradient',
      gradient
    });
  }, [onChange]);

  const handleSplitChange = useCallback((split: PageBackground['split']) => {
    onChange({
      type: 'split',
      split
    });
  }, [onChange]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-20 z-40"
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Palette className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Page Background</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Preview */}
        <div className="p-4 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-700 mb-2">Preview</div>
          <div 
            className="w-full h-20 rounded-lg border border-gray-200 shadow-sm"
            style={{ background: generateBackgroundCSS(background) }}
          />
        </div>

        {/* Quick Colors */}
        <div className="p-4 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-700 mb-3">Quick Colors</div>
          <div className="grid grid-cols-11 gap-1">
            {QUICK_COLORS.map((color, index) => (
              <button
                key={index}
                onClick={() => handleQuickColorSelect(color)}
                className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveSection('colors')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeSection === 'colors'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Palette className="w-4 h-4 inline mr-2" />
            Colors
          </button>
          <button
            onClick={() => setActiveSection('gradients')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeSection === 'gradients'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Layers className="w-4 h-4 inline mr-2" />
            Gradients
          </button>
          <button
            onClick={() => setActiveSection('split')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeSection === 'split'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Split className="w-4 h-4 inline mr-2" />
            Split
          </button>

        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeSection === 'colors' && (
            <div className="space-y-4">
              <button
                onClick={() => setShowColorPicker(true)}
                className="w-full p-3 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-8 h-8 rounded border border-gray-200"
                    style={{ backgroundColor: background.color || '#ffffff' }}
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Custom Color</div>
                    <div className="text-xs text-gray-500">{background.color || '#ffffff'}</div>
                  </div>
                </div>
              </button>
            </div>
          )}

          {activeSection === 'gradients' && (
            <div className="space-y-4">
              {/* Quick Gradient Presets */}
              <div>
                <div className="text-sm font-medium text-gray-700 mb-3">Popular Gradients</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleGradientChange({
                      type: 'linear',
                      angle: 45,
                      stops: [
                        { color: '#667eea', position: 0 },
                        { color: '#764ba2', position: 100 }
                      ]
                    })}
                    className="h-12 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                    style={{ background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)' }}
                    title="Ocean Blue"
                  />
                  <button
                    onClick={() => handleGradientChange({
                      type: 'linear',
                      angle: 45,
                      stops: [
                        { color: '#f093fb', position: 0 },
                        { color: '#f5576c', position: 100 }
                      ]
                    })}
                    className="h-12 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                    style={{ background: 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)' }}
                    title="Pink Sunset"
                  />
                  <button
                    onClick={() => handleGradientChange({
                      type: 'linear',
                      angle: 45,
                      stops: [
                        { color: '#4facfe', position: 0 },
                        { color: '#00f2fe', position: 100 }
                      ]
                    })}
                    className="h-12 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                    style={{ background: 'linear-gradient(45deg, #4facfe 0%, #00f2fe 100%)' }}
                    title="Sky Blue"
                  />
                  <button
                    onClick={() => handleGradientChange({
                      type: 'linear',
                      angle: 45,
                      stops: [
                        { color: '#43e97b', position: 0 },
                        { color: '#38f9d7', position: 100 }
                      ]
                    })}
                    className="h-12 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                    style={{ background: 'linear-gradient(45deg, #43e97b 0%, #38f9d7 100%)' }}
                    title="Fresh Green"
                  />
                </div>
              </div>

              {/* Custom Gradient Editor */}
              <button
                onClick={() => setShowGradientEditor(true)}
                className="w-full p-3 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="w-8 h-8 rounded border border-gray-200"
                    style={{
                      background: background.type === 'gradient'
                        ? generateBackgroundCSS(background)
                        : 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)'
                    }}
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Custom Gradient</div>
                    <div className="text-xs text-gray-500">
                      {background.type === 'gradient' ? background.gradient?.type || 'linear' : 'Click to create custom'}
                    </div>
                  </div>
                </div>
              </button>
            </div>
          )}

          {activeSection === 'split' && (
            <div className="space-y-4">
              {/* Quick Split Presets */}
              <div>
                <div className="text-sm font-medium text-gray-700 mb-3">Popular Split Backgrounds</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleSplitChange({
                      direction: 'horizontal',
                      position: 50,
                      first: { type: 'solid', color: '#667eea' },
                      second: { type: 'solid', color: '#764ba2' }
                    })}
                    className="h-16 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors relative overflow-hidden"
                    title="Blue Horizontal Split"
                  >
                    <div className="absolute inset-0">
                      <div className="h-1/2 bg-blue-500"></div>
                      <div className="h-1/2 bg-purple-600"></div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-xs font-medium text-white bg-black bg-opacity-50 px-2 py-1 rounded">
                        Horizontal
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleSplitChange({
                      direction: 'vertical',
                      position: 50,
                      first: { type: 'solid', color: '#43e97b' },
                      second: { type: 'solid', color: '#38f9d7' }
                    })}
                    className="h-16 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors relative overflow-hidden"
                    title="Green Vertical Split"
                  >
                    <div className="absolute inset-0 flex">
                      <div className="w-1/2 bg-green-500"></div>
                      <div className="w-1/2 bg-teal-400"></div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-xs font-medium text-white bg-black bg-opacity-50 px-2 py-1 rounded">
                        Vertical
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleSplitChange({
                      direction: 'horizontal',
                      position: 50,
                      first: { type: 'solid', color: '#f093fb' },
                      second: { type: 'solid', color: '#f5576c' }
                    })}
                    className="h-16 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors relative overflow-hidden"
                    title="Pink Horizontal Split"
                  >
                    <div className="absolute inset-0">
                      <div className="h-1/2 bg-pink-400"></div>
                      <div className="h-1/2 bg-red-500"></div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-xs font-medium text-white bg-black bg-opacity-50 px-2 py-1 rounded">
                        Horizontal
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleSplitChange({
                      direction: 'vertical',
                      position: 50,
                      first: { type: 'solid', color: '#4facfe' },
                      second: { type: 'solid', color: '#00f2fe' }
                    })}
                    className="h-16 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors relative overflow-hidden"
                    title="Sky Vertical Split"
                  >
                    <div className="absolute inset-0 flex">
                      <div className="w-1/2 bg-blue-400"></div>
                      <div className="w-1/2 bg-cyan-400"></div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-xs font-medium text-white bg-black bg-opacity-50 px-2 py-1 rounded">
                        Vertical
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Current Split Editor (if split is active) */}
              {background.type === 'split' && background.split && (
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-700 mb-3">Edit Current Split</div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Direction:</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSplitChange({
                            ...background.split!,
                            direction: 'horizontal'
                          })}
                          className={`px-3 py-1 text-xs rounded ${
                            background.split.direction === 'horizontal'
                              ? 'bg-blue-100 text-blue-700 border border-blue-300'
                              : 'bg-gray-100 text-gray-600 border border-gray-300'
                          }`}
                        >
                          Horizontal
                        </button>
                        <button
                          onClick={() => handleSplitChange({
                            ...background.split!,
                            direction: 'vertical'
                          })}
                          className={`px-3 py-1 text-xs rounded ${
                            background.split.direction === 'vertical'
                              ? 'bg-blue-100 text-blue-700 border border-blue-300'
                              : 'bg-gray-100 text-gray-600 border border-gray-300'
                          }`}
                        >
                          Vertical
                        </button>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-600 mb-2">Colors:</div>
                      <div className="flex space-x-2">
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 mb-1">First Section</div>
                          <button
                            onClick={() => {
                              // TODO: Open color picker for first section
                              console.log('Edit first section color');
                            }}
                            className="w-full h-8 rounded border border-gray-300 hover:border-blue-300 transition-colors"
                            style={{ backgroundColor: background.split.first.color || '#667eea' }}
                            title="Click to change first section color"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 mb-1">Second Section</div>
                          <button
                            onClick={() => {
                              // TODO: Open color picker for second section
                              console.log('Edit second section color');
                            }}
                            className="w-full h-8 rounded border border-gray-300 hover:border-blue-300 transition-colors"
                            style={{ backgroundColor: background.split.second.color || '#764ba2' }}
                            title="Click to change second section color"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


        </div>
      </div>

      {/* Color Picker Modal - Rendered as Portal */}
      {showColorPicker && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowColorPicker(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <AdvancedColorPicker
              color={background.color || '#ffffff'}
              onChange={(color) => {
                onChange({ type: 'solid', color });
              }}
              onClose={() => setShowColorPicker(false)}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Gradient Editor Modal - Rendered as Portal */}
      {showGradientEditor && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998]"
          onClick={() => setShowGradientEditor(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <GradientEditor
              gradient={background.gradient || {
                type: 'linear',
                angle: 45,
                stops: [
                  { color: '#667eea', position: 0 },
                  { color: '#764ba2', position: 100 }
                ]
              }}
              onChange={handleGradientChange}
              onClose={() => setShowGradientEditor(false)}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
